
import React, { createContext, useEffect, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '../../integrations/supabase/client';
import { useToast } from '../../hooks/use-toast';
import { AuthContextType } from './types';
import { 
  signInWithCredentials, 
  signInWithGoogle as signInWithGoogleService, 
  signUpWithCredentials, 
  signOutUser,
  checkAdminRole
} from './authService';

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [initialSessionChecked, setInitialSessionChecked] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const checkAdminStatus = async () => {
    if (!user) return false;
    
    try {
      const isAdminUser = await checkAdminRole();
      setIsAdmin(isAdminUser);
      return isAdminUser;
    } catch (err) {
      console.error('Error checking admin status:', err);
      setIsAdmin(false);
      return false;
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        console.log('Auth state changed:', event);
        
        // Only update session and user state
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        if (event === 'SIGNED_IN') {
          // Check admin status when user signs in
          checkAdminStatus();
          
          // Only navigate to home on explicit sign-in actions
          if (!initialSessionChecked) {
            // Initial session - don't navigate
            console.log('check initial')
            setInitialSessionChecked(true);
          }
        } else if (event === 'SIGNED_OUT') {
          setIsAdmin(null);
          
          // Only navigate on explicit sign-out
          setTimeout(() => {
            navigate('/auth');
          }, 0);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setInitialSessionChecked(true);
      
      if (currentSession?.user) {
        checkAdminStatus();
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const signIn = async (email: string, password: string) => {
    try {
      setLoading(true);
      await signInWithCredentials(email, password);
      
      toast({
        title: 'เข้าสู่ระบบสำเร็จ',
        description: 'ยินดีต้อนรับกลับมา',
      });
    } catch (error: any) {
      console.error('Error signing in:', error);
      toast({
        title: 'เกิดข้อผิดพลาดในการเข้าสู่ระบบ',
        description: error?.message || 'โปรดตรวจสอบอีเมลและรหัสผ่านของคุณ',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    try {
      setLoading(true);
      await signInWithGoogleService();
      
      // No need for toast here as we will be redirected to Google's auth page
    } catch (error: any) {
      console.error('Error signing in with Google:', error);
      toast({
        title: 'เกิดข้อผิดพลาดในการเข้าสู่ระบบด้วย Google',
        description: error?.message || 'โปรดลองอีกครั้งในภายหลัง',
        variant: 'destructive',
      });
      setLoading(false);
    }
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      setLoading(true);
      await signUpWithCredentials(email, password, fullName);
      
      toast({
        title: 'สมัครสมาชิกสำเร็จ',
        description: 'โปรดตรวจสอบอีเมลของคุณเพื่อยืนยัน',
      });
    } catch (error: any) {
      console.error('Error signing up:', error);
      toast({
        title: 'เกิดข้อผิดพลาดในการสมัครสมาชิก',
        description: error?.message || 'โปรดลองอีกครั้งในภายหลัง',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setLoading(true);
      await signOutUser();
      toast({
        title: 'ออกจากระบบสำเร็จ',
      });
    } catch (error: any) {
      console.error('Error signing out:', error);
      toast({
        title: 'เกิดข้อผิดพลาดในการออกจากระบบ',
        description: error?.message || 'โปรดลองอีกครั้งในภายหลัง',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const value = {
    session,
    user,
    signIn,
    signUp,
    signInWithGoogle,
    signOut,
    loading,
    isAdmin,
    checkAdminStatus,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
